/**
 * 全域錯誤處理 Hook
 * 提供統一的錯誤處理機制（支援 SWR）
 */
import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { ApiError } from '../lib/fetcher';

export type { ApiError };

/**
 * 錯誤處理 Hook
 */
export function useErrorHandler() {
  const handleError = useCallback((error: unknown, context?: string) => {
    console.error(`Error in ${context || 'unknown context'}:`, error);

    let errorMessage = '發生未知錯誤';
    let errorObj: Error;

    if (error instanceof Error) {
      errorMessage = error.message;
      errorObj = error;
    } else if (typeof error === 'string') {
      errorMessage = error;
      errorObj = new Error(error);
    } else if (error && typeof error === 'object') {
      // 處理 SWR 錯誤（ApiError 類別）
      if (error instanceof ApiError) {
        errorMessage = error.message;
        errorObj = error;
      } else {
        // 處理其他格式的錯誤回應（向後相容）
        const apiError = error as {
          response?: { data?: { message?: string; error?: string } };
          message?: string;
          status?: number;
        };
        if (apiError?.response?.data?.message) {
          errorMessage = apiError.response.data.message;
        } else if (apiError?.response?.data?.error) {
          errorMessage = apiError.response.data.error;
        } else if (apiError?.message) {
          errorMessage = apiError.message;
        }
        errorObj = new Error(errorMessage);
      }
    } else {
      errorObj = new Error(String(error));
    }

    // 發送錯誤到伺服器
    import('../utils/logger.js')
      .then(({ logErrorToServer }) => {
        logErrorToServer(errorObj, {
          severity: 'error',
          context: {
            type: 'useErrorHandler',
            context: context || 'unknown',
            originalError: String(error),
          },
        });
      })
      .catch(importError => {
        console.error('[useErrorHandler] 無法導入 logger:', importError);
      });

    // 顯示錯誤 toast
    toast.error(errorMessage);

    return errorMessage;
  }, []);

  const handleSuccess = useCallback((message: string) => {
    toast.success(message);
  }, []);

  const handleWarning = useCallback((message: string) => {
    toast(message, {
      icon: '⚠️',
      style: {
        background: '#F59E0B',
        color: '#fff',
      },
    });
  }, []);

  return {
    handleError,
    handleSuccess,
    handleWarning,
  };
}
